import * as THREE from "three";

/** A loop track's ends this close are one pose (metres / scale units). */
const SEAM_SLACK = 2e-3;
/** At most this many passes of a loop clip are chained to close its spinners' other tracks. */
const MAX_UNROLL = 8;

const nodeOf = (track: string): string => track.slice(0, track.lastIndexOf("."));

/**
 * A loop clip whose spinners' other tracks do not close (the Moonstone shards bob with the quarter turn: each ends
 * where the next began, so the bake looked whole only while the turn snapped back) is played through as many passes
 * as it takes for each such track to come back to its start, each pass taking the track of the part that started
 * where the last one ended. Every other track repeats. A track no other part continues is left as baked.
 */
export function closeSeams(clip: THREE.AnimationClip, spun: ReadonlySet<string>): THREE.AnimationClip {
  const open = clip.tracks.filter((tr) => spun.has(nodeOf(tr.name)) && !tr.name.endsWith(".quaternion") && seam(tr) > SEAM_SLACK);
  if (open.length === 0) return clip;
  const chains = new Map<THREE.KeyframeTrack, THREE.KeyframeTrack[]>();
  for (const tr of open) {
    const prop = tr.name.slice(tr.name.lastIndexOf("."));
    const chain = chainOf(tr, open.filter((o) => o.name.endsWith(prop)));
    if (chain && chain.length > 1) chains.set(tr, chain);
  }
  const passes = [...chains.values()].reduce((l, c) => lcm(l, c.length), 1);
  if (passes === 1 || passes > MAX_UNROLL) return clip;
  const tracks = clip.tracks.map((tr) => {
    const chain = chains.get(tr);
    return tile(tr, passes, clip.duration, (m) => (chain ? chain[m % chain.length] : tr));
  });
  return new THREE.AnimationClip(clip.name, clip.duration * passes, tracks, clip.blendMode);
}

const valueSize = (tr: THREE.KeyframeTrack): number => tr.values.length / tr.times.length;

/** The largest difference between a track's last and first values. */
export function seam(tr: THREE.KeyframeTrack): number {
  const s = valueSize(tr), last = tr.values.length - s;
  let d = 0;
  for (let i = 0; i < s; i++) d = Math.max(d, Math.abs(tr.values[last + i] - tr.values[i]));
  return d;
}

/** Tracks from `tr` on, each starting where the last ended, until one ends where `tr` started; null if none. */
function chainOf(tr: THREE.KeyframeTrack, pool: readonly THREE.KeyframeTrack[]): THREE.KeyframeTrack[] | null {
  const s = valueSize(tr);
  const meets = (a: THREE.KeyframeTrack, b: THREE.KeyframeTrack): boolean => {
    const end = a.values.length - s;
    for (let i = 0; i < s; i++) if (Math.abs(a.values[end + i] - b.values[i]) > SEAM_SLACK) return false;
    return true;
  };
  const chain = [tr];
  for (let k = 0; k < MAX_UNROLL; k++) {
    const last = chain[chain.length - 1];
    if (meets(last, tr)) return chain;
    // the next part round (the pool is in node order) that starts where this one ended
    const at = pool.indexOf(last);
    const next = [...pool.slice(at + 1), ...pool.slice(0, at + 1)].find((x) => valueSize(x) === s && meets(last, x));
    if (!next) return null;
    chain.push(next);
  }
  return null;
}

/** `tr` played `passes` times end to end, pass m keyed from `source(m)`; a key on the previous one's time is dropped. */
function tile(tr: THREE.KeyframeTrack, passes: number, length: number, source: (m: number) => THREE.KeyframeTrack): THREE.KeyframeTrack {
  const s = valueSize(tr), times: number[] = [], values: number[] = [];
  for (let m = 0; m < passes; m++) {
    const src = source(m);
    for (let i = 0; i < src.times.length; i++) {
      const t = src.times[i] + m * length;
      if (times.length && t <= times[times.length - 1]) continue;
      times.push(t);
      for (let j = 0; j < s; j++) values.push(src.values[i * s + j]);
    }
  }
  const out = tr.clone();
  out.times = new Float32Array(times);
  out.values = new Float32Array(values);
  return out;
}

const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
const lcm = (a: number, b: number): number => (a / gcd(a, b)) * b;
